//@ts-check
/**
 * What the Me sheet does — change your name or your handle, invite someone, delete your account —
 * and answering the age question and picking a handle
 * (wip-archive/social/SOCIAL_PHASE1_THE_DOOR.md, "The Me sheet", "Sign-in, then the age question";
 * wip-archive/social/SOCIAL_PHASE2_SHARING.md, Q1–Q3).
 *
 * Unlike the other sheets, these wait for the server's answer before they say anything. An invite
 * is a promise to another person, and deleting an account cannot be undone, so "done" has to be
 * true when it is shown. Each answers with what happened rather than throwing, and the sheet says
 * it in its own words.
 */
import ky, { isNetworkError, isTimeoutError } from "ky";
import { getfetchopts } from "$lib/utils/utils.js";
import { apiBase } from "$lib/env.js";
import { auth, forgetEverything, loadSession, sessionRefused } from "$lib/auth.svelte.js";
import { getLocaleTag } from "$lib/utils/locale.svelte.js";

const basePath = apiBase();

/**
 * Why a call did not do what was asked: no network, the session gone (the person is sent to sign
 * in), or anything else.
 *
 * @param {any} err
 * @returns {"offline" | "signed_out" | "failed"}
 */
export function failure(err) {
  if (isNetworkError(err) || isTimeoutError(err) || !navigator.onLine) return "offline";
  if (err?.isautherr) {
    sessionRefused();
    return "signed_out";
  }
  return "failed";
}

/**
 * @param {string} firstName
 * @param {string} lastName may be empty
 * @returns {Promise<{ ok: true } | { ok: false, reason: "offline" | "signed_out" | "failed" }>}
 */
export async function saveName(firstName, lastName) {
  try {
    const fetchopts = await getfetchopts({ json: { firstName, lastName } });
    const saved = /** @type {any} */ (await ky.put(`${basePath}/api/auth/me`, fetchopts).json());
    if (auth.user) auth.user = { ...auth.user, firstName: saved.firstName, lastName: saved.lastName };
    return { ok: true };
  } catch (err) {
    console.error("Failed to save the name", err);
    return { ok: false, reason: failure(err) };
  }
}

/**
 * Invite someone, in this app's language. An address that already has an account is answered
 * exactly as a new one (the server sends it nothing), so the sheet cannot tell, and neither can
 * the person inviting.
 *
 * @param {{ name: string, email: string }} invitee
 * @returns {Promise<{ ok: true, invitesLeft: number }
 *   | { ok: false, reason: "none_left" | "own_address" | "bad_email" | "offline" | "signed_out" | "failed" }>}
 */
export async function sendInvite({ name, email }) {
  try {
    const fetchopts = await getfetchopts({ json: { name, email, locale: getLocaleTag() } });
    const res = /** @type {any} */ (await ky.post(`${basePath}/api/auth/invites`, fetchopts).json());
    if (auth.user) auth.user = { ...auth.user, invitesLeft: res.invitesLeft };
    return { ok: true, invitesLeft: res.invitesLeft };
  } catch (err) {
    console.error("Failed to send the invite", err);
    const status = err?.response?.status;
    if (status === 409) {
      if (auth.user) auth.user = { ...auth.user, invitesLeft: 0 };
      return { ok: false, reason: "none_left" };
    }
    if (status === 400 && /own address/i.test(err?.body?.message ?? "")) return { ok: false, reason: "own_address" };
    // The server is the real check on an address, and refuses some the form lets through, such
    // as a top-level domain that does not exist. Trying again would not help.
    if (status === 400) return { ok: false, reason: "bad_email" };
    return { ok: false, reason: failure(err) };
  }
}

/**
 * Delete the account and everything in it, then forget it on this device, and go to sign-in,
 * which says it is done. Resolves only on failure: on success the page is already leaving.
 *
 * @returns {Promise<{ ok: false, reason: "offline" | "signed_out" | "failed" }>}
 */
export async function deleteAccount() {
  try {
    const fetchopts = await getfetchopts();
    await ky.delete(`${basePath}/api/auth/me`, fetchopts).json();
  } catch (err) {
    console.error("Failed to delete the account", err);
    return { ok: false, reason: failure(err) };
  }
  await forgetEverything();
  window.location.href = "/login?deleted=1";
  return new Promise(() => {});
}

/**
 * Answer the age question: the month and year someone was born, once (platform/VIABILITY.md, "Age,
 * asked once"). Under 13 the server deletes the account and says so, and this device forgets it.
 * An answer the server already has counts as answered: the question was answered elsewhere.
 *
 * @param {string} born `YYYY-MM`
 * @returns {Promise<{ ok: true, deleted: boolean } | { ok: false, reason: "offline" | "signed_out" | "failed" }>}
 */
export async function answerAge(born) {
  try {
    const fetchopts = await getfetchopts({ json: { born } });
    const res = /** @type {any} */ (await ky.put(`${basePath}/api/auth/me/born`, fetchopts).json());
    if (res.deleted) {
      await forgetEverything();
      return { ok: true, deleted: true };
    }
  } catch (err) {
    if (err?.response?.status !== 409) {
      console.error("Failed to answer the age question", err);
      return { ok: false, reason: failure(err) };
    }
  }
  // `/me` works out `adult` from the answer, so it is asked rather than guessed here.
  await loadSession();
  return { ok: true, deleted: false };
}

/**
 * Whether a handle can be had: free, already yours, or why not. Asked while someone types, so a
 * failure says only that it could not be checked; picking it is the real test.
 *
 * @param {string} handle as typed, without the `@`
 * @returns {Promise<{ ok: true, available: boolean, yours: boolean, code?: string }
 *   | { ok: false, reason: "offline" | "signed_out" | "failed" }>}
 */
export async function checkHandle(handle) {
  try {
    const fetchopts = await getfetchopts();
    const res = /** @type {any} */ (
      await ky.get(`${basePath}/api/auth/handles/${encodeURIComponent(handle)}`, fetchopts).json()
    );
    return { ok: true, available: Boolean(res.available), yours: Boolean(res.yours), code: res.code };
  } catch (err) {
    console.error("Failed to check the handle", err);
    return { ok: false, reason: failure(err) };
  }
}

/**
 * Pick a handle, or change it. A change keeps the old one for its owner for 14 days, so it can be
 * taken back.
 *
 * @param {string} handle as typed, without the `@`
 * @returns {Promise<{ ok: true, handle: string }
 *   | { ok: false, reason: "taken" | "length" | "characters" | "dots" | "reserved" | "offline" | "signed_out" | "failed" }>}
 */
export async function pickHandle(handle) {
  try {
    const fetchopts = await getfetchopts({ json: { handle } });
    const res = /** @type {any} */ (await ky.put(`${basePath}/api/auth/me/handle`, fetchopts).json());
    if (auth.user) auth.user = { ...auth.user, handle: res.handle };
    return { ok: true, handle: res.handle };
  } catch (err) {
    console.error("Failed to pick the handle", err);
    const status = err?.response?.status;
    if (status === 409) return { ok: false, reason: "taken" };
    const code = err?.body?.code;
    if (status === 400 && ["length", "characters", "dots", "reserved"].includes(code))
      return { ok: false, reason: code };
    return { ok: false, reason: failure(err) };
  }
}

/**
 * How far down the feed on Home you have looked: the time of the newest item it showed. What is
 * newer is marked _New_ next time, on every device. Only ever forward; if the server never hears,
 * the same items are marked again, which is the lesser harm.
 *
 * @param {string} at
 */
export async function markSeen(at) {
  if (!auth.user || (auth.user.feedSeen && auth.user.feedSeen >= at)) return;
  auth.user = { ...auth.user, feedSeen: at };
  try {
    const fetchopts = await getfetchopts({ json: { at } });
    await ky.put(`${basePath}/api/auth/me/seen`, fetchopts).json();
  } catch (err) {
    console.error("Failed to mark the feed seen", err);
    failure(err);
  }
}

/**
 * Put something away for good — Home's introduction or a notice, with _Got it_ — on the person's
 * record, so it stays gone on every device. It goes at once on this screen; if the server never
 * hears, it comes back the next time the app asks who you are, and can be put away again.
 *
 * @param {"intro" | "push" | `notice-${string}`} what a notice is `notice-` and its id (lib/notices.js);
 *   `push`, the card asking to turn on notifications
 */
export async function dismiss(what) {
  if (auth.user) auth.user = { ...auth.user, dismissed: [...new Set([...(auth.user.dismissed ?? []), what])] };
  try {
    const fetchopts = await getfetchopts();
    await ky.put(`${basePath}/api/auth/me/dismissed/${what}`, fetchopts).json();
  } catch (err) {
    console.error(`Failed to put away ${what}`, err);
    failure(err);
  }
}

/**
 * Turn one kind of notification on or off, from its switch in the Me sheet
 * (wip-archive/social/SOCIAL_PHASE5_NOTIFICATIONS.md, "Settings"). For every device of the
 * person's: iOS's own settings stay the switch for a phone. Waits for the server, as the rest of
 * the Me sheet does.
 *
 * @param {string} kind one of the server's kinds (lambdalib/notifications.js)
 * @param {boolean} on
 * @returns {Promise<{ ok: true } | { ok: false, reason: "offline" | "signed_out" | "failed" }>}
 */
export async function setNotify(kind, on) {
  try {
    const fetchopts = await getfetchopts({ json: { kind, on } });
    const res = /** @type {any} */ (await ky.put(`${basePath}/api/auth/me/notify`, fetchopts).json());
    if (auth.user) auth.user = { ...auth.user, notifyOff: res.notifyOff };
    return { ok: true };
  } catch (err) {
    console.error(`Failed to turn ${kind} ${on ? "on" : "off"}`, err);
    return { ok: false, reason: failure(err) };
  }
}
